import { EntityRefUtil, formatDateWithUserTimezone, GetUsersResponseDTO, UserStatus } from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { cn, initials } from '@/lib/utils';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';

const columnHelper = createColumnHelper<GetUsersResponseDTO['data'][number]>();

export const createSitemateUsersTableColumns = () => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.accessor('firstName', {
      id: 'firstName',
      header: 'First Name',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ row, table }) => {
        const user = row.original;
        const value = user.firstName || '-';
        const searchTerm = table.getState().globalFilter;
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(user.firstName || '');

        const userFullInitials = initials(EntityRefUtil.createUserFullNameRef(user));
        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={userFullInitials}
            avatarUrl={user.avatarUrl || ''}
            avatarBackgroundColorClass={avatarBackgroundColorClass}
            searchTerm={searchTerm}
          />
        );
      },
    }),
    columnHelper.accessor('lastName', {
      id: 'lastName',
      header: 'Last Name',
      enableGrouping: false,
      enableColumnFilter: false,
      cell: ({ getValue, table }) => {
        const value = getValue() || '-';
        const searchTerm = table.getState().globalFilter;
        return (
          <div className={cn('overflow-hidden overflow-ellipsis', value === '-' && 'text-secondary-text')}>
            {searchTerm ? highlightSearchMatch(value, searchTerm) : value}
          </div>
        );
      },
    }),
    columnHelper.accessor('companyName', {
      id: 'companyName',
      header: 'Company',
      enableGrouping: false,
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue() || '-';
        return <div className={cn('overflow-hidden overflow-ellipsis', value === '-' && 'text-secondary-text')}>{value}</div>;
      },
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: 'Date Joined',
      enableGrouping: false,
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ getValue }) => {
        const dateValue = getValue();
        const value = dateValue ? formatDateWithUserTimezone(new Date(dateValue), userTimezone) : '-';
        return <div className={cn('overflow-hidden overflow-ellipsis', value === '-' && 'text-secondary-text')}>{value}</div>;
      },
      meta: {
        cellClassName: 'w-[120px]',
      },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      enableGrouping: false,
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original;

        const badgeClasses = match(user.status ?? UserStatus.Active)
          .with(UserStatus.Active, () => 'bg-default-light-green text-default-dark-green')
          .otherwise(() => 'bg-grey-1 text-default-text');

        const statusText = match(user.status ?? UserStatus.Active)
          .with(UserStatus.Active, () => 'Active')
          .otherwise(() => '');

        const displayValue = (
          <div className="flex flex-row gap-5">
            <span className={cn('flex w-fit items-center gap-5 rounded-[5px] p-[2px_5px] text-sm font-medium', badgeClasses)}>{statusText}</span>
          </div>
        );
        return displayValue;
      },
      meta: {
        cellClassName: 'w-[110px]',
      },
    }),
  ] as AccessorKeyColumnDef<GetUsersResponseDTO['data'][number]>[];
};

function highlightSearchMatch(text: string, searchTerm: string) {
  if (!text || !searchTerm) return text;
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.split(regex).map((part) => (regex.test(part) ? <b>{part}</b> : part));
}
